import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { COLORS } from "../../styles/variables";

// Contenedor de la imagen con efecto de carga
const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${({ height }) => height || "auto"};
  overflow: hidden;
  background-color: ${COLORS.GRAY_LIGHT};
  border-radius: ${({ $borderRadius }) => $borderRadius || "0"};
`;

// Imagen con transición suave al cargar
const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) => $objectFit || "cover"};
  object-position: ${({ $objectPosition }) => $objectPosition || "center"};
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

// Placeholder mientras se carga la imagen
const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${COLORS.GRAY_LIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 0 : 1)};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

// Efecto de shimmer (brillo) mientras se carga
const ShimmerEffect = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${COLORS.GRAY_LIGHT} 0%,
    ${COLORS.GRAY_MEDIUM}22 50%,
    ${COLORS.GRAY_LIGHT} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

/**
 * Componente de imagen con carga perezosa y soporte para WebP
 * @param {Object} props - Propiedades del componente
 * @param {string} props.src - URL de la imagen
 * @param {string} props.webpSrc - URL de la imagen en formato WebP
 * @param {string} props.alt - Texto alternativo
 * @param {string} props.height - Altura de la imagen
 * @param {string} props.objectFit - Propiedad object-fit de CSS
 * @param {string} props.objectPosition - Propiedad object-position de CSS
 * @param {string} props.borderRadius - Radio de borde
 * @param {Object} props.imgProps - Propiedades adicionales para la etiqueta img
 */
const LazyImage = ({
  src,
  webpSrc,
  alt,
  height,
  objectFit,
  objectPosition,
  borderRadius,
  imgProps = {},
  ...rest
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [supportsWebP, setSupportsWebP] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Detectar soporte para WebP
  useEffect(() => {
    const checkWebPSupport = async () => {
      try {
        const webPCheck = new Image();
        webPCheck.onload = () => setSupportsWebP(true);
        webPCheck.onerror = () => setSupportsWebP(false);
        webPCheck.src =
          "data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==";
      } catch (e) {
        setSupportsWebP(false);
      }
    };

    checkWebPSupport();
  }, []);

  // Configurar Intersection Observer para carga perezosa
  useEffect(() => {
    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            // Usar WebP si está disponible y el navegador lo soporta
            if (webpSrc && supportsWebP) {
              img.src = webpSrc;
            } else {
              img.src = src;
            }

            // Desconectar el observer una vez que la imagen está en el viewport
            observerRef.current.disconnect();
          }
        });
      },
      {
        rootMargin: "200px 0px", // Cargar imágenes 200px antes de que sean visibles
        threshold: 0.01,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, webpSrc, supportsWebP]);

  // Manejar el evento de carga de la imagen
  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  return (
    <ImageContainer height={height} $borderRadius={borderRadius} {...rest}>
      <StyledImage
        ref={imgRef}
        alt={alt}
        onLoad={handleImageLoad}
        $isLoaded={isLoaded}
        $objectFit={objectFit}
        $objectPosition={objectPosition}
        loading="lazy"
        {...imgProps}
      />
      <Placeholder $isLoaded={isLoaded}>
        <ShimmerEffect />
      </Placeholder>
    </ImageContainer>
  );
};

export default LazyImage;
